import { useMemo } from 'react';
import { useAuthSessionInternal } from './auth-provider';
import { useFlowReducer, type FlowReducerConfig } from './use-flow-reducer';
import {
  signInReducer,
  signInEffects,
  signInDeriveFailure,
  isSignInLoading,
  type SignInState,
  type SignInAction,
  type SignInDeps,
} from '../flows/sign-in';
import { INITIAL_STATE } from '../flows/sign-in/reducer';

export interface UseSignInReturn {
  state: SignInState;
  isLoading: boolean;
  dispatch: (action: SignInAction) => void;

  status: SignInState['step'];
  identifier: string | null;
  error: string | null;
  userData: { firstName?: string; authMethods: string[] } | null;

  create: (params: { identifier: string }) => void;
  attemptFirstFactor: (params: { strategy: 'password'; password: string } | { strategy: 'reset_password'; password: string }) => void;
  attemptVerification: (params: { code: string }) => void;
  back: () => void;
  reset: () => void;
}

export function useSignIn(): UseSignInReturn {
  const session = useAuthSessionInternal();

  const config = useMemo((): FlowReducerConfig<SignInState, SignInAction, SignInDeps> => ({
    reducer: signInReducer,
    initialState: INITIAL_STATE,
    effects: signInEffects,
    deriveFailure: signInDeriveFailure,
    deps: {
      service: session.internals.service,
      observability: session.internals.observability,
      eventBus: session.internals.eventBus,
      session,
    },
  }), [session]);

  const [state, dispatch] = useFlowReducer(config);

  return {
    state,
    isLoading: isSignInLoading(state),
    dispatch,

    status: state.step,
    identifier: 'identifier' in state ? state.identifier : null,
    error: 'error' in state ? state.error : null,
    userData: 'userData' in state ? state.userData : null,

    create: params => dispatch({ type: 'SUBMIT_EMAIL', identifier: params.identifier }),
    attemptFirstFactor: params => {
      if (params.strategy === 'password') {
        dispatch({ type: 'SUBMIT_PASSWORD', password: params.password });
      } else {
        dispatch({ type: 'SUBMIT_RESET_PASSWORD', password: params.password });
      }
    },
    attemptVerification: params => dispatch({ type: 'SUBMIT_VERIFICATION', code: params.code }),
    back: () => dispatch({ type: 'BACK' }),
    reset: () => dispatch({ type: 'RESET' }),
  };
}
